import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { useNotificationContext } from '@/Context/NotificationContext';
import useCompanyId from '@/Hooks/UseCompanyId';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';
import { useEditRecruitmentContext } from '../../Edit/Contexts/EditRecruitmentContext';
import StepProps from '../StepProps';
import { ExperienceCriteriaForm, mapRhfCriteriaToExperienceCriteria } from './Components/ExperienceCriteriaForm';
import IdentifyExperienceStep from './IdentifyExperienceStep';
import ExperienceStepRecalculationDialog from './Components/ExperienceStepRecalculationDialog';

const EditIdentifyExperienceStep = ({ activeStep, handleBack }: StepProps): React.JSX.Element => {
    const navigate = useNavigate();
    const companyId = useCompanyId();

    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const [triggerBlocker, setTriggerBlocker] = useState<boolean>(false);

    const {
        processingState,
        experienceCriteriaProcessingState,
        experienceCriteria,
        setExperienceCriteria: setExperienceCriteriaForm,
        resetExperienceCriteriaProcessingState,
        client,
        updateRecruitmentWithExperienceCriteriaAsync,
        isExperienceScoreRecalculationComplete,
        pollExperienceCriteriaScoreRecalculationStateAsync,
    } = useEditRecruitmentContext();

    const navigateAwayFromPage = () => {
        showSuccessToaster(UIStrings.RecruitmentUpdated);

        // navigate to client recruitment overview.
        const createRecruitmentPath = generatePath(RoutePath.AssessmentsActive, {
            companyId,
            clientId: client.id,
        });
        navigate(createRecruitmentPath);
    };

    const onSubmit = async ({ criteria }: ExperienceCriteriaForm) => {
        try {
            await updateRecruitmentWithExperienceCriteriaAsync(criteria.map(mapRhfCriteriaToExperienceCriteria));
            // if the update recruitment first polling of recalculation state gives "incomplete/executing" state, block and poll
            setTriggerBlocker(true);
        } catch {
            showFailToaster(UIStrings.WeAreStillWorkingOnYourPreviousRecalculationRequest);
        }
    };

    useEffect(() => {
        const pollingIntervalId = setInterval(async () => {
            if (triggerBlocker) {
                if (isExperienceScoreRecalculationComplete) {
                    setTriggerBlocker(false);
                    navigateAwayFromPage();
                } else {
                    await pollExperienceCriteriaScoreRecalculationStateAsync();
                }
            }
        }, 1000); // Poll the backend every 1000ms

        return () => {
            clearInterval(pollingIntervalId); // Clean up on unmount
        };
    }, [triggerBlocker, isExperienceScoreRecalculationComplete]);

    return (
        <>
            <IdentifyExperienceStep
                activeStep={activeStep}
                handleBack={handleBack}
                processingState={processingState}
                experienceCriteriaProcessingState={experienceCriteriaProcessingState}
                experienceCriteria={experienceCriteria}
                setExperienceCriteria={setExperienceCriteriaForm}
                resetExperienceCriteriaProcessingState={resetExperienceCriteriaProcessingState}
                onSubmit={onSubmit}
                isEditMode
            />
            <ExperienceStepRecalculationDialog open={triggerBlocker && !isExperienceScoreRecalculationComplete} />
        </>
    );
};

export default EditIdentifyExperienceStep;
